import { forwardRef, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

function assignRef(ref, node) {
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
}

function readSelection(el) {
    try {
        return { start: el.selectionStart, end: el.selectionEnd };
    } catch {
        return { start: null, end: null };
    }
}

/**
 * Password field with an in-input show/hide control.
 * Starts masked. The toggle is type="button" so it never submits a form.
 */
const PasswordInput = forwardRef(function PasswordInput(
    { className, disabled, ...props },
    forwardedRef
) {
    const [visible, setVisible] = useState(false);
    const inputRef = useRef(null);

    const setInputRef = (node) => {
        inputRef.current = node;
        assignRef(forwardedRef, node);
    };

    // Keep the caret in the field when the icon is clicked. Keyboard users
    // who have moved focus onto the button keep that focus.
    const keepFieldFocus = (event) => {
        const el = inputRef.current;
        if (el && document.activeElement === el) event.preventDefault();
    };

    const toggleVisibility = () => {
        const el = inputRef.current;
        const focusInput = Boolean(el && document.activeElement === el);
        const selection = el ? readSelection(el) : { start: null, end: null };
        flushSync(() => {
            setVisible((current) => !current);
        });
        if (!focusInput || !el) return;
        el.focus();
        if (selection.start == null || selection.end == null) return;
        try {
            el.setSelectionRange(selection.start, selection.end);
        } catch {
            // Some browsers reject selection ranges on masked inputs.
        }
    };

    return (
        <div className="password-field">
            <input
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                {...props}
                ref={setInputRef}
                disabled={disabled}
                type={visible ? "text" : "password"}
                className={cn("fsrs-input", className)}
            />
            <button
                type="button"
                className="password-field-toggle"
                onMouseDown={keepFieldFocus}
                onClick={toggleVisibility}
                disabled={disabled}
                aria-label={visible ? "Hide password" : "Show password"}
                aria-pressed={visible}
                data-testid="password-visibility-toggle"
            >
                {visible ? (
                    <EyeOff aria-hidden="true" size={18} strokeWidth={1.75} />
                ) : (
                    <Eye aria-hidden="true" size={18} strokeWidth={1.75} />
                )}
            </button>
        </div>
    );
});

PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
